/**
 * ÉLŐ KÖZVETÍTÉS — a rögzítés publikusan olvasható tükre.
 *
 * A `cloudSync.ts` a SAJÁT eszközeid közt szinkronizál (`users/{uid}/private/
 * tracking`, csak a tulajdonos olvashatja). Ez a modul ennek a nyilvános
 * párja: a `liveStreams/{uid}` dokumentumot BÁRKI olvashatja, mert ez táplálja
 * a `/live/:username` nézőoldalt és az OBS-overlayt.
 *
 * ⚠️ EZÉRT MÁS A SZABÁLYRENDSZERE, MINT A CLOUDSYNC-NEK:
 *
 *   1. Csak akkor ír, ha a felhasználó a közvetítést bekapcsolta. 1.18.0 óta
 *      a kapcsoló bekapcsolva MARAD (Geri döntése) — ezért a kikapcsolásnak
 *      mindent le kell állítania, a natív feltöltőt és a szerveroldali adást
 *      is (lásd `closeStream` és a natív kulcs törlése lent).
 *   2. ⚠️ A NYOMVONAL VÁGATLANUL MEGY KI — sem a rajt, sem a cél védőköre
 *      nincs levágva. Ez tudatos vállalás, Geri kérése (2026-09-22): élő
 *      adásnál az a cél, hogy a pozíció AZONNAL látszódjon, és a streamer
 *      helyzete a párhuzamosan futó térképes szolgáltatásokból amúgy is
 *      nyilvános. A vágás korábban a rajt körüli 200 métert kötelezően
 *      elrejtette; ettől viszont a rögzítés első perce üresen maradt.
 *
 *      Aki ezt később visszatenné: a `trimPrivateEnds` a CÉL védőkörét az
 *      utolsó pontra alapozza, élőben pedig az a jelenlegi pozíció — vagyis
 *      épp azt tüntetné el, amiért a nézet létezik.
 *   3. A kiírt pillanatkép KÉSLELTETETT. Alapértelmezésben 15 másodperccel
 *      korábbi állapotot mutat, mint ami épp igaz. A Twitch-adás maga is késik
 *      ennyit, tehát a nézőnek szinkronban marad — viszont a link önmagában
 *      nem használható valós idejű helymeghatározásra.
 *
 * ⚠️ NATÍV APPBAN MÁS AZ ÚT (2026-09-24). iOS-en zárt képernyőnél a WebView
 * JavaScriptje felfüggesztődik, tehát ez a hook nem fut — az adás
 * megállna. Natívon ezért a nyomvonalat, a távot és az időt a SZERVER írja
 * a natív réteg által küldött mintákból (`server/src/routes/live.ts`); ez a
 * hook ilyenkor csak adáskulcsot kér, átadja a natív rétegnek, és a
 * `cells*` mezőket írja merge-dzsel. Egyetlen író a nyomvonalra — lásd
 * `docs/ai/terv-2026-09-24-live-hatter.md`.
 *
 * Weben a kliens ír közvetlenül, nem a szerveren át: ez ugyanaz a mintázat, mint a
 * `cloudSync`, és NEM ütközik a „kliens sosem ír játékadatot" szabállyal —
 * itt egy mulandó pillanatkép készül, nem terület, GP, szint vagy jelvény.
 * A játékbeli eredményt továbbra is kizárólag a mentés dönti el a szerveren.
 */
import { useCallback, useEffect, useRef } from 'react';
import { deleteDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { api, API_BASE } from '@/lib/api';
import { configureNativeLive } from './nativeSource';
import { currentSpeedMps, movingMs, type RecorderState } from './recorder';
import { publicRoute } from './liveRoute';
import { liveCellStats, resetLiveCellStats, type LiveCellStats } from './liveCellStats';
import type { ActivityType, TracePoint } from '@/types';

/**
 * Sűrűbb, mint a `cloudSync` 15 másodperce: ott egy másik eszköz nézi néha,
 * itt egy élő adás fut. Egy óra közvetítés ~1200 Firestore-írás, ami a
 * kiírás pillanatában elhanyagolható költség.
 */
const PUBLISH_INTERVAL_MS = 3_000;

/**
 * A NATÍV küldés üteme — a késleltetéshez igazítva (Geri döntése, 2026-09-24,
 * akkumulátor). A 3 másodperces küldés a mobilrádiót gyakorlatilag végig
 * ébren tartotta. Késleltetett adásnál a néző úgyis a `delayMs`-sel korábbi
 * állapotot látja, ott a sűrű küldés semmit nem ad; késleltetés nélkül a
 * térkép simasága miatt marad rövidebb. A szívverés (15 s) ettől független.
 */
const NATIVE_LIVE_INTERVAL_MS = 5_000;
const NATIVE_LIVE_DELAYED_INTERVAL_MS = 10_000;

export function nativeLiveIntervalMs(delayMs: number): number {
  return delayMs > 0 ? NATIVE_LIVE_DELAYED_INTERVAL_MS : NATIVE_LIVE_INTERVAL_MS;
}

/**
 * A puffer ennél régebbi képkockáit eldobjuk. A leghosszabb választható
 * csúszás kétszerese — marad tartalék a ritkuló tickekre (háttérbe tett app).
 */
const BUFFER_SPAN_MS = 120_000;

export interface LivePublishOptions {
  /** Csak akkor írunk, ha ez igaz. A rögzítés indításakor kell bekapcsolni. */
  enabled: boolean;
  /** A nézőoldalon megjelenő név; a `/live/:username` ezt oldja fel. */
  username: string;
  displayName: string;
  /** A profilkép címe — a `/live/battle` láblécében látszik. */
  photoURL: string | null;
  /** A profilban választott cellaszín kulcsa — a `/live/battle` ezzel színez. */
  cellColor: string | null;
  /** Szándékos csúszás ezredmásodpercben. */
  delayMs: number;
  /**
   * Natív app: a nyomvonalat a szerver írja a natív mintákból (lásd a fájl
   * fejlécét). Weben mindig `false`.
   */
  native: boolean;
}

/** A szerveroldali mintafogadás egy kérésben ennyit fogad. */
const BACKLOG_CHUNK = 600;

/**
 * Az adáskulcs kérése előtt rögzített pontok feltöltése — amit a natív réteg
 * a `configureLive` ELŐTT mért, azt már nem küldi el. A szerver időbélyeg
 * szerint szűri a duplikátumot, tehát az átfedés ártalmatlan.
 */
async function sendBacklog(uid: string, key: string, state: RecorderState): Promise<void> {
  if (state.startedAt === null) return;
  const activity = {
    startedAt: state.startedAt,
    pausedMs: state.pausedMs,
    pausedAt: state.pausedAt,
    status: state.status === 'paused' ? 'paused' : 'recording',
  };
  const points = state.points;
  for (let from = 0; from === 0 || from < points.length; from += BACKLOG_CHUNK) {
    const samples = points.slice(from, from + BACKLOG_CHUNK).map((point) => ({
      t: point.t,
      lat: point.lat,
      lng: point.lng,
      accuracy: point.accuracy ?? 99_999,
    }));
    await fetch(`${API_BASE}/api/live/samples/${encodeURIComponent(uid)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Live-Key': key },
      body: JSON.stringify({ now: Date.now(), samples, activity }),
    });
  }
}

/** Natív adás állapota egy rögzítésre. */
interface NativeSession {
  /** A rögzítés azonosítója, amire a kulcs szól. */
  recordingId: string;
  /** Igaz, ha a natív réteg átvette a nyomvonal küldését. */
  active: boolean;
  /** A legutóbb kiírt cellaszámok — változatlanul nem írunk újra. */
  lastCells: string;
}

/** Egy pillanatkép, amit KÉSŐBB fogunk kiírni. */
export interface BufferedFrame {
  at: number;
  status: RecorderState['status'];
  type: ActivityType;
  points: TracePoint[];
  distanceM: number;
  movingMs: number;
  speedMps: number | null;
  startedAt: number | null;
  /**
   * A MEZŐ-bontás a képkocka pillanatában. A képkockával EGYÜTT késik, hogy a
   * számok ne árulják el előre, mi történik éppen most.
   */
  cells: LiveCellStats;
}

/**
 * A publikus dokumentum alakja. Szándékosan SZŰK: ami nincs benne, az nem is
 * szivároghat ki. Nincs benne uid, e-mail, eszközazonosító, sem a mentetlen
 * aktivitás azonosítója.
 */
export interface LiveStreamDoc {
  /** Public reads are limited to streams created by the app owner. */
  broadcasterRole: 'owner';
  username: string;
  displayName: string;
  photoURL: string | null;
  cellColor: string | null;
  status: 'recording' | 'paused' | 'finished';
  type: ActivityType;
  /** Polyline-kódolt, privátzónára vágott, egyszerűsített nyomvonal. */
  route: string;
  distanceM: number;
  movingMs: number;
  speedMps: number | null;
  /** A kiírt pillanatkép VALÓDI kora — ennyivel csúszik az adás. */
  delayMs: number;
  /**
   * Élő BECSLÉS a rögzítő előnézetéből: mi lenne, ha most fejeznéd be. A
   * végleges eredményt a mentés dönti el a szerveren.
   */
  cellsNew: number;
  cellsStolen: number;
  cellsDefended: number;
  updatedAt: unknown;
}

/**
 * Elindítja a közvetítést, amíg a rögzítés fut és `enabled` igaz.
 *
 * Nem ad vissza semmit: a hívónak nincs mit megjelenítenie belőle, az
 * állapotot a saját felülete ismeri.
 */
export function useLivePublish(
  uid: string | undefined,
  state: RecorderState,
  options: LivePublishOptions,
): void {
  const latest = useRef(state);
  const config = useRef(options);
  const buffer = useRef<BufferedFrame[]>([]);
  const wasEnabled = useRef(false);
  const previousStatus = useRef<RecorderState['status']>('idle');
  const nativeSession = useRef<NativeSession | null>(null);
  latest.current = state;
  config.current = options;

  /**
   * NATÍV ADÁS INDÍTÁSA — rögzítésenként egyszer.
   *
   * Ha bármi elhasal (nincs hálózat, régebbi natív build), az adás a webes
   * úton megy tovább: az előtérben ugyanúgy frissül, csak zárt képernyőnél
   * áll meg — pontosan a mai viselkedés.
   */
  useEffect(() => {
    if (!options.native || !options.enabled || !uid) return;
    if (state.status !== 'recording' && state.status !== 'paused') return;
    if (nativeSession.current?.recordingId === state.id) return;
    const session: NativeSession = { recordingId: state.id, active: false, lastCells: '' };
    nativeSession.current = session;
    void (async () => {
      try {
        const { key } = await api.liveOpenSession({ delayMs: config.current.delayMs, type: latest.current.type });
        const endpoint = `${API_BASE}/api/live/samples/${encodeURIComponent(uid)}`;
        if (nativeSession.current !== session) return;
        const intervalMs = nativeLiveIntervalMs(config.current.delayMs);
        if (!(await configureNativeLive({ endpoint, key, intervalMs }))) {
          await api.liveCloseSession().catch(() => undefined);
          return;
        }
        session.active = true;
        await sendBacklog(uid, key, latest.current).catch(() => undefined);
      } catch {
        /* marad a webes út */
      }
    })();
  }, [options.native, options.enabled, uid, state.status, state.id]);

  /**
   * KIKAPCSOLT KÖZVETÍTÉSNÉL A NATÍV RÉTEG BIZTOSAN NE KÜLDJÖN — rögzítésenként
   * egyszer, az induláskor.
   *
   * ⚠️ Az Android az adáskulcsot TARTÓSAN tárolja (a folyamat újraindulását is
   * túl kell élnie), az iOS a folyamat élettartamáig. Egy korábbi, rendesen le
   * nem zárt adás kulcsa így egy KÖVETKEZŐ, közvetítés nélküli rögzítést is
   * kiküldhetett volna — ébrentartással és sűrű mintavétellel együtt.
   */
  const clearedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!options.native || options.enabled) return;
    if (state.status !== 'recording' && state.status !== 'paused') return;
    if (clearedFor.current === state.id) return;
    clearedFor.current = state.id;
    void configureNativeLive(null);
  }, [options.native, options.enabled, state.status, state.id]);

  const publish = useCallback(async () => {
    if (!db || !uid) return;
    const { enabled, username, displayName, photoURL, cellColor, delayMs } = config.current;
    if (!enabled) return;

    const current = latest.current;
    if (current.status === 'idle') return;

    const now = Date.now();
    buffer.current.push({
      at: now,
      status: current.status,
      type: current.type,
      points: [...current.points],
      distanceM: current.distanceM,
      movingMs: movingMs(current, now),
      speedMps: currentSpeedMps(current),
      startedAt: current.startedAt,
      cells: liveCellStats(),
    });
    dropStaleFrames(buffer.current, now);

    const frame = frameToPublish(buffer.current, now, delayMs);
    if (!frame) return;

    const session = nativeSession.current;
    if (session?.active) {
      /* A nyomvonalat a szerver írja; innen csak a MEZŐ-bontás megy, a
         képkockával együtt késleltetve. Az első szerveroldali írás előtt a
         szabály elutasítja (hiányzik a státusz) — az a következő ütemben
         magától rendeződik. */
      const cellsKey = JSON.stringify(frame.cells);
      if (cellsKey === session.lastCells) return;
      await setDoc(doc(db, 'liveStreams', uid), { ...frame.cells }, { merge: true });
      session.lastCells = cellsKey;
      return;
    }
    if (frame.status !== 'recording' && frame.status !== 'paused' && frame.status !== 'finished') {
      return;
    }

    const payload: LiveStreamDoc = {
      broadcasterRole: 'owner',
      username,
      displayName,
      photoURL,
      cellColor,
      status: frame.status,
      type: frame.type,
      route: publicRoute(frame.points),
      distanceM: frame.distanceM,
      movingMs: frame.movingMs,
      speedMps: frame.speedMps,
      delayMs: Math.max(0, now - frame.at),
      ...frame.cells,
      updatedAt: serverTimestamp(),
    };
    await setDoc(doc(db, 'liveStreams', uid), payload);
  }, [uid]);

  /**
   * A LEÁLLÍTÁS — és miért nem a teljes nyomvonallal zárunk.
   *
   * A csúszás miatt a befejezés pillanatában a puffer végén még ott van
   * `delayMs`-nyi ki nem írt útvonal. Ha ezt záráskor kiírnánk, azzal pont azt
   * fednénk fel, amit a csúszás véd: hol vagy ÉPPEN MOST. Ezért a záró
   * dokumentum nyomvonal NÉLKÜL megy ki, csak a végösszegekkel — a teljes
   * útvonalat a nézők a rendes aktivitás-oldalon látják majd, a szokásos
   * privátzóna-vágással.
   */
  const closeStream = useCallback(async (final: BufferedFrame | null) => {
    if (!db || !uid) return;
    buffer.current = [];
    resetLiveCellStats();
    const session = nativeSession.current;
    nativeSession.current = null;
    if (session?.active) {
      /* A natív küldés leállítása ELŐBB: a szerver a munkamenet törlése után
         úgyis elutasítaná, de fölösleges kéréseket ne küldjünk. */
      await configureNativeLive(null);
      await api.liveCloseSession(
        final ? { distanceM: final.distanceM, movingMs: final.movingMs } : undefined,
      ).catch(() => undefined);
      return;
    }
    if (!final) {
      await deleteDoc(doc(db, 'liveStreams', uid)).catch(() => undefined);
      return;
    }
    const { username, displayName, photoURL, cellColor } = config.current;
    const payload: LiveStreamDoc = {
      broadcasterRole: 'owner',
      username,
      displayName,
      photoURL,
      cellColor,
      status: 'finished',
      type: final.type,
      route: '',
      distanceM: final.distanceM,
      movingMs: final.movingMs,
      speedMps: null,
      delayMs: 0,
      ...final.cells,
      updatedAt: serverTimestamp(),
    };
    await setDoc(doc(db, 'liveStreams', uid), payload);
  }, [uid]);

  // A közvetítés kikapcsolása azonnal hasson: a dokumentum eltűnik, a
  // nézőoldal „nincs élő rögzítés" állapotba kerül.
  useEffect(() => {
    if (options.enabled) {
      wasEnabled.current = true;
      return;
    }
    if (!wasEnabled.current) return;
    wasEnabled.current = false;
    void closeStream(null);
  }, [options.enabled, closeStream]);

  /**
   * A rögzítés vége zárja az adást is.
   *
   * ⚠️ Kizárólag státuszVÁLTÁSRA fut, nem minden renderre. A `RecorderState`
   * minden elfogadott GPS-pontnál új objektum; ha attól függne, a `finished`
   * állapotban másodpercenként újraírná ugyanazt a záró dokumentumot.
   */
  useEffect(() => {
    if (state.status === previousStatus.current) return;
    const previous = previousStatus.current;
    previousStatus.current = state.status;
    if (previous === 'idle') resetLiveCellStats();
    if (!options.enabled) return;
    if (state.status !== 'idle' && state.status !== 'finished') return;
    if (previous === 'idle') return; // sosem indult el: nincs mit lezárni
    const snapshot = latest.current;
    const now = Date.now();
    const final: BufferedFrame | null = state.status === 'finished'
      ? {
          at: now,
          status: 'finished',
          type: snapshot.type,
          points: [],
          distanceM: snapshot.distanceM,
          movingMs: movingMs(snapshot, now),
          speedMps: null,
          startedAt: snapshot.startedAt,
          cells: liveCellStats(),
        }
      : null;
    void closeStream(final);
  }, [options.enabled, state.status, closeStream]);

  // A rendes ütem. Az első kiírás azonnal megtörténik, hogy a nézőoldal ne
  // várjon egy teljes ciklust az „élő" állapotra.
  //
  // CSAK RÖGZÍTÉS ALATT fut (2026-09-24, akkumulátor): a bekapcsolva maradó
  // közvetítés mellett korábban rögzítés nélkül, háttérben is 3 másodpercenként
  // felébresztette a JavaScriptet — a `publish` ilyenkor úgyis azonnal kilépett.
  const recording = state.status === 'recording' || state.status === 'paused';
  useEffect(() => {
    if (!uid || !options.enabled || !recording) return;
    void publish().catch(() => undefined);
    const timer = window.setInterval(() => {
      void publish().catch(() => undefined);
    }, PUBLISH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [uid, options.enabled, recording, publish]);
}

/**
 * A kiírandó képkocka kiválasztása.
 *
 * A legfrissebb olyan képkocka, amely már legalább `delayMs` ideje megvan. Ha
 * ilyen még nincs (a rögzítés épp indult), a LEGRÉGEBBIT adjuk — az a
 * legkevésbé friss, tehát a legbiztonságosabb. Így az adás azonnal látszik,
 * de a csúszás garanciáját nem sérti.
 */
export function frameToPublish(
  frames: readonly BufferedFrame[],
  now: number,
  delayMs: number,
): BufferedFrame | null {
  if (frames.length === 0) return null;
  if (delayMs <= 0) return frames[frames.length - 1] ?? null;
  const cutoff = now - delayMs;
  for (let i = frames.length - 1; i >= 0; i -= 1) {
    const frame = frames[i]!;
    if (frame.at <= cutoff) return frame;
  }
  return frames[0] ?? null;
}

function dropStaleFrames(frames: BufferedFrame[], now: number): void {
  const cutoff = now - BUFFER_SPAN_MS;
  let drop = 0;
  while (drop < frames.length - 1 && frames[drop]!.at < cutoff) drop += 1;
  if (drop > 0) frames.splice(0, drop);
}

export { publicRoute };
